import { indexDataValue, type IndexData } from "../../../types/Codes";
import type { ElakeArvioModel, IncomeDevelopment } from "../../../types/ElakeArvioModel";

export type AccumulationScheduleContext = {
  readonly calculationYear: number;
  readonly calculationMonth: number;
};

export type AccumulationSchedule = {
  readonly startYear: number;
  readonly pensionYear: number;
  readonly pensionMonth: number;
  readonly exceptionIndexing: boolean;
  /** Age at end of the calendar year before startYear (loop increments before use). */
  readonly ageBeforeStartYear: number;
};

/**
 * Loop bounds and pension timing. Normalizes pensionAgeMonth 0 on model in place.
 */
export function resolveAccumulationSchedule(
  model: ElakeArvioModel,
  context: AccumulationScheduleContext,
): AccumulationSchedule {
  let startYear = context.calculationYear;
  if (context.calculationMonth === 12) {
    startYear += 1;
  }

  if (model.pensionAgeMonth === 0) {
    model.pensionAgeYear -= 1;
    model.pensionAgeMonth = 12;
  }

  let pensionMonth = model.birthMonth + model.pensionAgeMonth + 1;
  while (pensionMonth > 12) {
    pensionMonth -= 12;
  }

  let pensionYear = model.birthYear + model.pensionAgeYear;
  let exceptionIndexing = false;
  if (model.birthMonth + model.pensionAgeMonth >= 12) {
    pensionYear += 1;

    // näillä henkilöillä laskenta yms. toimii oikein eläkevuodella, indeksointi pitää ulottaa yhden vuoden pidemmälle ja lisätä eläkevuoteen lopuksi yksi
    if (model.birthMonth + model.pensionAgeMonth >= 24) {
      exceptionIndexing = true;
    }
  }

  return {
    startYear,
    pensionYear,
    pensionMonth,
    exceptionIndexing,
    ageBeforeStartYear: startYear - model.birthYear - 1,
  };
}

/** Default annual accrual rate (1.5 %). */
export const DEFAULT_ACCUMULATING_PERCENT = 0.015;

/** Transition rule rate 2017–2025 (1.7 %). */
export const TRANSITION_ACCUMULATING_PERCENT = 0.017;

export type AccumulatingPercentInput = {
  readonly year: number;
  readonly age: number;
  readonly birthMonth: number;
  readonly pensionAgeYear: number;
  readonly pensionAgeMonth: number;
};

/**
 * Karttumaprosentin siirtymäsäännös (2017–2025) and default rate by calendar year and age.
 * Pure function extracted from pension accumulation loops.
 */
export function accumulatingPercentForYear(
  input: AccumulatingPercentInput,
): number {
  const { year, age, birthMonth, pensionAgeYear, pensionAgeMonth } = input;

  let accumulatingPercent = DEFAULT_ACCUMULATING_PERCENT;
  if (year >= 2017 && year <= 2025 && age >= 54 && age <= 62) {
    accumulatingPercent = TRANSITION_ACCUMULATING_PERCENT;
  } else if (year >= 2017 && year <= 2025 && age === 53) {
    accumulatingPercent =
      (birthMonth / 12) * DEFAULT_ACCUMULATING_PERCENT +
      ((12 - birthMonth) / 12) * TRANSITION_ACCUMULATING_PERCENT;
  } else if (
    year >= 2017 &&
    year <= 2025 &&
    age === 63 &&
    (pensionAgeYear !== 63 || birthMonth + pensionAgeMonth > 12)
  ) {
    accumulatingPercent =
      (birthMonth / 12) * TRANSITION_ACCUMULATING_PERCENT +
      ((12 - birthMonth) / 12) * DEFAULT_ACCUMULATING_PERCENT;
  } else if (year >= 2017 && year <= 2025 && age === 63 && pensionAgeYear === 63) {
    accumulatingPercent =
      (birthMonth / (birthMonth + pensionAgeMonth)) *
        TRANSITION_ACCUMULATING_PERCENT +
      (pensionAgeMonth / (pensionAgeMonth + birthMonth)) *
        DEFAULT_ACCUMULATING_PERCENT;
  }
  return accumulatingPercent;
}

/** Upper age for new pension accrual by birth cohort. */
export function accrualLimitForBirthYear(birthYear: number): number {
  if (birthYear < 1958) {
    return 68;
  }
  if (birthYear >= 1958 && birthYear <= 1961) {
    return 69;
  }
  return 70;
}

export type AccrualMonthsInput = {
  readonly year: number;
  readonly startYear: number;
  readonly pensionYear: number;
  readonly age: number;
  readonly birthYear: number;
  readonly birthMonth: number;
  readonly pensionAgeYear: number;
  readonly pensionAgeMonth: number;
  readonly calculationMonth: number;
};

/**
 * Accrual months in a calendar year (pension year partial months, upper age cap, TYEL-395).
 */
export function accrualMonthsForYear(input: AccrualMonthsInput): number {
  const {
    year,
    startYear,
    pensionYear,
    age,
    birthYear,
    birthMonth,
    pensionAgeYear,
    pensionAgeMonth,
    calculationMonth,
  } = input;

  let accrualMonths = 0;
  if (year !== pensionYear) {
    accrualMonths = 12;
  } else if (pensionAgeYear === age) {
    // Huom. vanhuuseläkeiän täyttämiskuukautena tehdään vielä töitä
    accrualMonths = pensionAgeMonth + birthMonth;
  } else {
    accrualMonths = pensionAgeMonth - (12 - birthMonth);
  }

  const accrualLimit = accrualLimitForBirthYear(birthYear);
  if (age === accrualLimit) {
    accrualMonths = Math.min(accrualMonths, birthMonth);
  } else if (age > accrualLimit) {
    accrualMonths = 0;
  }

  // 17.5.2023 TYEL-395 alkuvuoden toteutuneet karttumat ovat jo syötetyssä karttumassa
  if (year === startYear && calculationMonth < 12) {
    accrualMonths -= calculationMonth;
  }
  if (accrualMonths < 0) {
    accrualMonths = 0;
  }

  return accrualMonths;
}

export type SalaryMultipliersInput = {
  readonly year: number;
  readonly incomeDevelopment: IncomeDevelopment;
  readonly indexData?: IndexData[];
};

export type SalaryMultipliersForYear = {
  /** Multiplier applied to income after the start year. */
  readonly forIncome: number;
  /** Palkkakerroin before static override; used for pension in startYear + 1. */
  readonly projectedPalkkakerroin: number;
};

/**
 * Annual salary growth multiplier (projected Palkkakerroin or static KHI ratio).
 */
export function salaryMultipliersForYear(
  input: SalaryMultipliersInput,
): SalaryMultipliersForYear {
  const { year, incomeDevelopment, indexData } = input;
  const projectedPalkkakerroin =
    indexDataValue(year, "Palkkakerroin", indexData) || 1;

  if (incomeDevelopment !== "static") {
    return { forIncome: projectedPalkkakerroin, projectedPalkkakerroin };
  }

  const khi = indexDataValue(year, "KHI", indexData) || 1;
  const khiPrevYear = indexDataValue(year - 1, "KHI", indexData) || 1;
  return {
    forIncome: khi / khiPrevYear,
    projectedPalkkakerroin,
  };
}
